import loginManager, { WalletLoginError, WALLET_LOGIN_ERROR } from '@/utils/login';
import { loginWithWallet } from '@/api/auth';
import {
  getUserId,
  getWalletAddress,
  getWalletProviderId,
  hasAuthSession,
  persistLoginResponse,
  setLoginWalletProviderId,
  setOriginLoginType,
  setWalletAddress,
  setWalletProviderId,
} from '@/utils/auth-token';
import { invalidateAuthSession, subscribeAuthState, validateAuthSession } from '@/utils/auth-session';
import { runAuthSoftRehydrate } from '@/composables/useAuthHydration';
import { reloadWatchlistOnAccountSwitch } from '@/composables/useMarketWatchlist';
import { resetAllFeedStores } from '@/utils/reset-feed-stores';
import { getWalletProviderById } from '@/utils/wallet-provider';
import {
  ensureWalletConnectReady,
  signWalletLoginCredentials,
} from '@/utils/wallet-connect-session';
import { registerSelfCustodyTradeAddress } from '@/utils/trade-address';

/** 支持 EIP-1193 accountsChanged 的 EVM 钱包（含 WalletConnect） */
const SYNC_WALLET_IDS = new Set(['metamask', 'okx', 'coinbase', 'bitget', 'walletconnect']);

let detachListener = null;
let paused = false;
let switchInProgress = false;
let debounceTimer = null;
let initialized = false;

export function setWalletAccountSyncPaused(value) {
  paused = Boolean(value);
}

function normalizeAddress(value) {
  return String(value ?? '').trim();
}

function addressesEqual(a, b) {
  const left = normalizeAddress(a).toLowerCase();
  const right = normalizeAddress(b).toLowerCase();
  return Boolean(left && right && left === right);
}

function isSyncEligible() {
  if (paused || switchInProgress || typeof window === 'undefined') return false;
  if (!hasAuthSession()) return false;
  return SYNC_WALLET_IDS.has(
    String(getWalletProviderId() ?? '')
      .trim()
      .toLowerCase()
  );
}

function teardownListener() {
  if (detachListener) {
    detachListener();
    detachListener = null;
  }
}

async function setupListener() {
  teardownListener();
  if (!isSyncEligible()) return;

  const providerId = String(getWalletProviderId() ?? '')
    .trim()
    .toLowerCase();
  if (providerId === 'walletconnect') {
    await ensureWalletConnectReady();
  }

  const provider = getWalletProviderById(providerId);
  if (!provider || typeof provider.on !== 'function') return;

  const onAccountsChanged = (accounts) => {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      void handleAccountsChanged(accounts);
    }, 400);
  };

  provider.on('accountsChanged', onAccountsChanged);
  detachListener = () => {
    if (typeof provider.removeListener === 'function') {
      provider.removeListener('accountsChanged', onAccountsChanged);
    }
  };
}

async function performWalletAccountSwitch() {
  const providerId = String(getWalletProviderId() ?? '')
    .trim()
    .toLowerCase();
  if (!SYNC_WALLET_IDS.has(providerId)) return;

  switchInProgress = true;
  const prevUserId = getUserId() || '';
  const prevAddress = getWalletAddress();
  const prevProviderId = getWalletProviderId();
  try {
    let creds;
    if (providerId === 'walletconnect') {
      creds = await signWalletLoginCredentials();
    } else {
      creds = await loginManager.loginWith(providerId);
    }

    const data = await loginWithWallet({
      walletAddress: creds.address,
      signature: creds.signature,
      message: creds.message,
      addressType: 1,
      originLoginType: 'WALLET',
    });

    // 仅登录成功后再写本地钱包态，避免失败时地址与 token 错位
    setWalletProviderId(providerId);
    setWalletAddress(creds.address);
    setOriginLoginType('WALLET');
    setLoginWalletProviderId(providerId);
    persistLoginResponse(data);

    const nextUserId = getUserId() || '';
    if (prevUserId && nextUserId && prevUserId !== nextUserId) {
      resetAllFeedStores();
    }
    reloadWatchlistOnAccountSwitch();

    try {
      await registerSelfCustodyTradeAddress(creds.address);
    } catch (err) {
      console.warn('[wallet-sync] registerSelfCustodyTradeAddress failed', err);
    }
    validateAuthSession();
    await runAuthSoftRehydrate();
  } catch (err) {
    if (err instanceof WalletLoginError && err.code === WALLET_LOGIN_ERROR.REJECTED) {
      console.warn('[wallet-sync] account switch sign rejected');
      return;
    }
    console.warn('[wallet-sync] account switch failed', err);
    // 回滚切号前地址 / provider，避免半更新
    if (prevAddress) setWalletAddress(prevAddress);
    if (prevProviderId) {
      setWalletProviderId(prevProviderId);
      setLoginWalletProviderId(prevProviderId);
    }
  } finally {
    switchInProgress = false;
  }
}

async function handleAccountsChanged(accounts) {
  if (!isSyncEligible()) return;

  const nextAddress = normalizeAddress(Array.isArray(accounts) ? accounts[0] : '');
  const currentAddress = getWalletAddress();

  if (!nextAddress) {
    // 断开账户：清会话并离开 requiresAuth 页
    invalidateAuthSession({ redirectHome: true });
    return;
  }

  if (addressesEqual(nextAddress, currentAddress)) return;

  await performWalletAccountSwitch();
}

/** 登录态变化时挂载/卸载 accountsChanged 监听 */
export function initWalletAccountSync() {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  subscribeAuthState(() => {
    void setupListener();
  });
  void setupListener();
}
